/**
 * Instance naming — the single source of truth mapping a rule (or decision
 * relation) plus the ids of its left-hand-side-bound rows to a stable instance
 * name, and back.
 *
 * A grounded constraint or variable is named `<qualifier>_<id>_<id>…`, where the
 * qualifier is the **rule name** (for a constraint) or the **decision-relation
 * name** (for a variable), and the ids are the keys of the rows bound on the
 * left. e.g. for `def dcpf(l: line, t: timestep)` the ids are the line
 * id and the timestep, so dcpf on edge3 at hour 0 is `dcpf_edge3_0`.
 *
 * Underscore-joined because HiGHS LP names are bare tokens — no spaces or
 * brackets — so the AMPL-style `dcpf[edge3, 0]` isn't available; the "[indices]"
 * live structurally instead. The same qualifier is stored on the grounded
 * object's `group` and the ids on its `indices`, so mapping a solution entry back
 * to its rule is normally a structured lookup (join the solution to the model
 * object by name, read `group`). When only the name string is on hand,
 * {@link parseInstanceName} recovers the same split using the known qualifiers.
 */

import { IndexValue } from '../highs';

/** Relations whose index renders with a `t` prefix in names (period indices). */
const TIME_RELATIONS = new Set(['timestep', 'soc_time']);

/**
 * Render one index value for a name. Values from a time relation get a `t`
 * prefix (`0` → `t0`), matching the model's convention; all others are raw. The
 * structured `indices` metadata keeps the raw value — only the name string is
 * decorated.
 */
export function formatIndexValue(relation: string, value: IndexValue): string {
  return TIME_RELATIONS.has(relation) ? `t${value}` : String(value);
}

/** The LP-safe instance name for a qualifier and its (already-rendered) ids. */
export function instanceName(qualifier: string, ids: IndexValue[]): string {
  return [qualifier, ...ids].join('_');
}

export interface ParsedInstance {
  qualifier: string;
  ids: string[];
}

/**
 * Recover (qualifier, ids) from an instance name, given the set of qualifiers it
 * could belong to. Picks the longest qualifier the name starts with — so
 * `initial_soc` wins over a shorter accidental prefix, and an underscore in the
 * qualifier is handled — then splits the remainder on `_`. Returns null if no
 * qualifier matches.
 */
export function parseInstanceName(name: string, qualifiers: string[]): ParsedInstance | null {
  const match = qualifiers
    .filter((q) => name === q || name.startsWith(`${q}_`))
    .sort((a, b) => b.length - a.length)[0];
  if (match === undefined) return null;
  const rest = name.slice(match.length);
  const ids = rest.startsWith('_') ? rest.slice(1).split('_') : [];
  return { qualifier: match, ids };
}
